import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DownOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Popover } from 'antd';
import { BaseModal, Button } from 'components/elements';
import {
  useCategoriesQuery,
  useGetAllGroupsLazyQuery,
  CategoriesQuery,
  GetAllGroupsDocument,
  GetAllGroupsQuery,
} from 'graphql/types';
import { Link, useHistory } from 'react-router-dom';
import GroupCard from 'components/groupCard';
import { Spinner } from 'components/elements';
import { breakpoints } from 'settings/style';
import { useMediaQuery } from 'hooks/useMediaQuery';
import {
  OuterCard,
  RootSection,
  RootHeader,
  RootAvatar,
  SubAvatar,
  SubAvatarPlaceholder,
  RootPanel,
  NestedList,
  NestedRowOuter,
  NestedRowMain,
  NestedChevron,
  HoverPreviewCard,
  HoverPreviewImage,
  HoverPreviewImagePlaceholder,
  HoverPreviewText,
  HoverPreviewTitle,
  InlineGroups,
  PanelHint,
  GroupsGrid,
  BrowseAll,
  DetailBody,
  DetailCover,
  DetailCoverPlaceholder,
  DetailTitleBlock,
  DetailTitle,
  DetailGloss,
  DetailDescription,
  DetailActions,
} from './categoriesList.styled';

type CategoryNode = CategoriesQuery['categories']['data'][0];
type GroupListItem = NonNullable<NonNullable<GetAllGroupsQuery['groups']>['data']>[number];

const BLOGS_PATH = '/community';

function isGoftemanCategoryTitle(title?: string | null) {
  return (title ?? '').trim().startsWith('Goftemān');
}

function formatRootMetaLine(category: CategoryNode | undefined, subs: CategoryNode[], groupCount: number | undefined) {
  if (!category) return '\u00a0';
  if (subs.length > 0) {
    const n = subs.length;
    return n === 1 ? '1 Subcategory' : `${n} Subcategories`;
  }
  if (groupCount != null) return `${groupCount} Group${groupCount === 1 ? '' : 's'}`;
  return '\u00a0';
}

function truncateDescription(text: string | undefined | null, maxLen: number) {
  const t = (text || '').trim();
  if (!t) return 'No description yet.';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen).trim()}…`;
}

const resolveSubCategories = (parent: CategoryNode | undefined, all: CategoryNode[]): CategoryNode[] => {
  if (!parent) return [];
  const ids = parent.attributes?.subCategories?.data?.map((s) => s.id) ?? [];
  return ids
    .map((sid) => all.find((c) => String(c.id) === String(sid)))
    .filter((c): c is CategoryNode => Boolean(c))
    .sort((a, b) => (a.attributes.order > b.attributes.order ? 1 : -1));
};

function isCategoryUnderRoot(activeId: string, rootId: string, all: CategoryNode[]): boolean {
  if (String(activeId) === String(rootId)) return true;
  let node = all.find((c) => String(c.id) === String(activeId));
  const seen = new Set<string>();
  while (node?.attributes?.parent?.data?.id) {
    const pid = String(node.attributes.parent.data.id);
    if (pid === String(rootId)) return true;
    if (seen.has(pid)) break;
    seen.add(pid);
    node = all.find((c) => String(c.id) === pid);
  }
  return false;
}

function groupsFilterForCategory(categoryId: string) {
  return {
    and: [{ status: { eq: 'approved' } }, { categories: { id: { in: [categoryId] } } }],
  };
}

export type CategoriesListMode = 'browse' | 'filter';

export type CategoriesListProps = {
  /** Homepage: inline group cards. Groups page: filter list below. */
  mode?: CategoriesListMode;
  selectedCategoryId?: string | null;
  onCategoryChange?: (categoryId: string | null) => void;
};

type CategoryTreeProps = {
  parentId: string;
  depth: number;
  allCategories: CategoryNode[];
  expandedIds: Record<string, boolean>;
  toggleExpand: (id: string) => void;
  activeGroupsCategoryId: string | null;
  onSelectCategory: (id: string) => void;
  onOpenDetail: (node: CategoryNode) => void;
  groupLoading: boolean;
  groupsData: GroupListItem[];
  isMobile: boolean;
  showInlineGroups: boolean;
};

const CategoryGroupsPanel = ({
  categoryId,
  loading,
  items,
  depth,
  flushLeft,
}: {
  categoryId: string;
  loading: boolean;
  items: GroupListItem[];
  depth: number;
  flushLeft?: boolean;
}) => (
  <InlineGroups $depth={depth} $flushLeft={Boolean(flushLeft)}>
    {loading ? (
      <Spinner />
    ) : items.length > 0 ? (
      <>
        <GroupsGrid>
          {items.map((g) => (
            <GroupCard key={g.id} {...g} />
          ))}
        </GroupsGrid>
        <BrowseAll to={`/category/${categoryId}`}>View all on category page</BrowseAll>
      </>
    ) : (
      <PanelHint>
        No groups in this category yet. <Link to={`/category/${categoryId}`}>Open category</Link>
      </PanelHint>
    )}
  </InlineGroups>
);

const HoverPreviewContent = ({
  coverUrl,
  title,
  description,
}: {
  coverUrl?: string | null;
  title?: string | null;
  description?: string | null;
}) => (
  <HoverPreviewCard>
    {coverUrl ? (
      <HoverPreviewImage $url={coverUrl} aria-hidden />
    ) : (
      <HoverPreviewImagePlaceholder aria-hidden />
    )}
    <HoverPreviewText>
      <HoverPreviewTitle>{title}</HoverPreviewTitle>
      {truncateDescription(description, 220)}
    </HoverPreviewText>
  </HoverPreviewCard>
);

const CategoryTree = ({
  parentId,
  depth,
  allCategories,
  expandedIds,
  toggleExpand,
  activeGroupsCategoryId,
  onSelectCategory,
  onOpenDetail,
  groupLoading,
  groupsData,
  isMobile,
  showInlineGroups,
}: CategoryTreeProps) => {
  const history = useHistory();
  const parent = allCategories.find((c) => String(c.id) === String(parentId));
  const children = resolveSubCategories(parent, allCategories);
  if (!children.length) return null;

  return (
    <NestedList $depth={depth}>
      {children.map((child, index) => {
        const subs = resolveSubCategories(child, allCategories);
        const hasChildren = subs.length > 0;
        const isExpanded = Boolean(expandedIds[String(child.id)]);
        const isActive = String(activeGroupsCategoryId) === String(child.id);
        const isLast = index === children.length - 1;
        const gloss = child.attributes?.translatedName;
        const desc = child.attributes?.description;

        const handleRowClick = () => {
          if (isGoftemanCategoryTitle(child.attributes?.title)) {
            history.push(BLOGS_PATH);
            return;
          }
          onOpenDetail(child);
        };

        const rowMain = (
          <NestedRowMain type="button" onClick={handleRowClick}>
            {child.attributes?.coverPhotoUrl ? (
              <SubAvatar src={child.attributes.coverPhotoUrl} alt="" />
            ) : (
              <SubAvatarPlaceholder aria-hidden />
            )}
            <span className="label">
              {child.attributes?.title}
              {gloss ? <span className="gloss"> {gloss}</span> : null}
            </span>
          </NestedRowMain>
        );

        return (
          <React.Fragment key={child.id}>
            <NestedRowOuter $depth={depth} $active={isActive} $isLast={isLast}>
              {isMobile ? (
                rowMain
              ) : (
                <Popover
                  mouseEnterDelay={0.45}
                  placement="rightTop"
                  destroyTooltipOnHide
                  content={
                    <HoverPreviewContent
                      coverUrl={child.attributes?.coverPhotoUrl}
                      title={child.attributes?.title}
                      description={desc}
                    />
                  }
                >
                  {rowMain}
                </Popover>
              )}
              <NestedChevron
                type="button"
                aria-label={hasChildren ? (isExpanded ? 'Collapse' : 'Expand') : 'Show groups'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (hasChildren) toggleExpand(String(child.id));
                  else onSelectCategory(String(child.id));
                }}
              >
                {hasChildren ? isExpanded ? <DownOutlined /> : <RightOutlined /> : <RightOutlined />}
              </NestedChevron>
            </NestedRowOuter>
            {showInlineGroups && isActive ? (
              <CategoryGroupsPanel
                categoryId={String(child.id)}
                loading={groupLoading}
                items={groupsData}
                depth={depth}
              />
            ) : null}
            {hasChildren && isExpanded ? (
              <CategoryTree
                parentId={String(child.id)}
                depth={depth + 1}
                allCategories={allCategories}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
                activeGroupsCategoryId={activeGroupsCategoryId}
                onSelectCategory={onSelectCategory}
                onOpenDetail={onOpenDetail}
                groupLoading={groupLoading}
                groupsData={groupsData}
                isMobile={isMobile}
                showInlineGroups={showInlineGroups}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </NestedList>
  );
};

const CategoriesList = ({
  mode = 'browse',
  selectedCategoryId = null,
  onCategoryChange,
}: CategoriesListProps) => {
  const isFilterMode = mode === 'filter';
  const showInlineGroups = !isFilterMode;
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.md}px)`);
  const { data } = useCategoriesQuery();
  const client = useApolloClient();
  const [openRootId, setOpenRootId] = useState<string | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Record<string, boolean>>({});
  const [browseActiveCategoryId, setBrowseActiveCategoryId] = useState<string | null>(null);
  const [groupCounts, setGroupCounts] = useState<Record<string, number>>({});
  const [detailCategory, setDetailCategory] = useState<CategoryNode | null>(null);

  const activeGroupsCategoryId = isFilterMode ? selectedCategoryId : browseActiveCategoryId;

  const allCategories = data?.categories?.data ?? [];

  const parentCategories = useMemo(
    () =>
      allCategories
        .filter((category) => !category?.attributes?.parent?.data)
        .sort((a, b) => (a?.attributes?.order > b?.attributes?.order ? 1 : -1)),
    [allCategories],
  );

  const parentCategoryIdsKey = useMemo(
    () =>
      allCategories
        .filter((c) => !c?.attributes?.parent?.data)
        .map((c) => c.id)
        .sort()
        .join(','),
    [allCategories],
  );

  const openParent = useMemo(
    () => allCategories.find((c) => String(c.id) === String(openRootId)),
    [allCategories, openRootId],
  );

  const rootSubCount = useMemo(() => resolveSubCategories(openParent, allCategories).length, [openParent, allCategories]);

  useEffect(() => {
    if (!parentCategoryIdsKey) return;
    const ids = parentCategoryIdsKey.split(',').filter(Boolean);
    let cancelled = false;

    (async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const { data: result } = await client.query({
              query: GetAllGroupsDocument,
              variables: {
                filters: groupsFilterForCategory(id),
                pagination: { page: 1, pageSize: 1 },
                sort: 'createdAt:desc',
              },
              fetchPolicy: 'cache-first',
            });
            next[id] = result?.groups?.meta?.pagination?.total ?? 0;
          } catch {
            next[id] = 0;
          }
        }),
      );
      if (!cancelled) setGroupCounts((prev) => ({ ...prev, ...next }));
    })();

    return () => {
      cancelled = true;
    };
  }, [parentCategoryIdsKey, client]);

  const [getGroups, { data: groupResult, loading: groupLoading }] = useGetAllGroupsLazyQuery();

  const fetchGroups = useCallback(
    (id: string) => {
      getGroups({
        variables: {
          pagination: { page: 1, pageSize: 12 },
          sort: 'createdAt:desc',
          filters: groupsFilterForCategory(id),
        },
      });
    },
    [getGroups],
  );

  useEffect(() => {
    if (!showInlineGroups || !activeGroupsCategoryId) {
      return;
    }
    fetchGroups(activeGroupsCategoryId);
  }, [activeGroupsCategoryId, fetchGroups, showInlineGroups]);

  useEffect(() => {
    if (!openRootId || rootSubCount !== 0) {
      return;
    }
    if (isFilterMode) {
      onCategoryChange?.(openRootId);
      return;
    }
    setBrowseActiveCategoryId(openRootId);
  }, [openRootId, rootSubCount, isFilterMode, onCategoryChange]);

  const toggleRoot = (id: string) => {
    const isClosing = openRootId === id;
    setOpenRootId((prev) => (prev === id ? null : id));
    setExpandedNodeIds({});
    if (isFilterMode) {
      if (
        isClosing &&
        selectedCategoryId &&
        (String(selectedCategoryId) === id || isCategoryUnderRoot(selectedCategoryId, id, allCategories))
      ) {
        onCategoryChange?.(null);
      }
      return;
    }
    setBrowseActiveCategoryId(null);
  };

  const toggleExpand = useCallback((id: string) => {
    setExpandedNodeIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onSelectCategory = useCallback(
    (id: string) => {
      if (isFilterMode) {
        onCategoryChange?.(selectedCategoryId === id ? null : id);
        return;
      }
      setBrowseActiveCategoryId(id);
    },
    [isFilterMode, onCategoryChange, selectedCategoryId],
  );

  const onOpenDetail = useCallback((node: CategoryNode) => {
    setDetailCategory(node);
  }, []);

  const closeDetail = useCallback(() => setDetailCategory(null), []);

  const showGroupsFromModal = useCallback(() => {
    if (!detailCategory?.id) return;
    const id = String(detailCategory.id);
    if (isFilterMode) {
      onCategoryChange?.(id);
    } else {
      setBrowseActiveCategoryId(id);
    }
    closeDetail();
  }, [detailCategory, closeDetail, isFilterMode, onCategoryChange]);

  const groupsData = groupResult?.groups?.data ?? [];

  const detailGloss = detailCategory
    ? detailCategory.attributes?.translatedName
    : null;

  return (
    <>
      <OuterCard>
        {parentCategories.map((category) => {
          const id = String(category?.id);
          const isOpen = openRootId === id;
          const subs = resolveSubCategories(category, allCategories);
          const count = groupCounts[id];
          const rootGloss = category?.attributes?.translatedName;
          const metaLine = formatRootMetaLine(category, subs, count);

          return (
            <RootSection key={category?.id} $isOpen={isOpen}>
              <RootHeader type="button" onClick={() => toggleRoot(id)} aria-expanded={isOpen} $open={isOpen}>
                <RootAvatar src={category?.attributes.coverPhotoUrl} alt="" />
                <div className="text">
                  <div className="title-line">
                    <span className="title">{category?.attributes?.title}</span>
                    {rootGloss ? <span className="gloss"> {rootGloss}</span> : null}
                  </div>
                  <div className="meta">{metaLine}</div>
                </div>
                <span className="root-chevron" aria-hidden>
                  {isOpen ? <UpOutlined /> : <DownOutlined />}
                </span>
              </RootHeader>
              {isOpen && (
                <RootPanel>
                  {subs.length > 0 ? (
                    <CategoryTree
                      parentId={id}
                      depth={0}
                      allCategories={allCategories}
                      expandedIds={expandedNodeIds}
                      toggleExpand={toggleExpand}
                      activeGroupsCategoryId={activeGroupsCategoryId}
                      onSelectCategory={onSelectCategory}
                      onOpenDetail={onOpenDetail}
                      groupLoading={groupLoading}
                      groupsData={groupsData}
                      isMobile={isMobile}
                      showInlineGroups={showInlineGroups}
                    />
                  ) : (
                    <PanelHint>
                      {isFilterMode
                        ? 'Select this category to filter the groups listed below.'
                        : 'Showing groups in this category.'}
                    </PanelHint>
                  )}

                  {showInlineGroups &&
                    subs.length === 0 &&
                    activeGroupsCategoryId &&
                    openRootId === id &&
                    isCategoryUnderRoot(activeGroupsCategoryId, openRootId, allCategories) && (
                      <CategoryGroupsPanel
                        categoryId={activeGroupsCategoryId}
                        loading={groupLoading}
                        items={groupsData}
                        depth={0}
                        flushLeft
                      />
                    )}

                  {isFilterMode && activeGroupsCategoryId && isCategoryUnderRoot(activeGroupsCategoryId, id, allCategories) ? (
                    <PanelHint>Showing groups in this category below.</PanelHint>
                  ) : null}
                </RootPanel>
              )}
            </RootSection>
          );
        })}
      </OuterCard>

      <BaseModal
        title={null}
        open={Boolean(detailCategory)}
        onCancel={closeDetail}
        footer={null}
        width={520}
        destroyOnClose
        centered
      >
        {detailCategory && (
          <DetailBody>
            {detailCategory.attributes?.coverPhotoUrl ? (
              <DetailCover src={detailCategory.attributes.coverPhotoUrl} alt="" />
            ) : (
              <DetailCoverPlaceholder />
            )}
            <DetailTitleBlock>
              <DetailTitle>{detailCategory.attributes?.title}</DetailTitle>
              {detailGloss ? <DetailGloss>{detailGloss}</DetailGloss> : null}
            </DetailTitleBlock>
            <DetailDescription>
              {detailCategory.attributes?.description?.trim() || 'No description for this category yet.'}
            </DetailDescription>
            <DetailActions>
              <Button type="primary" onClick={showGroupsFromModal}>
                {isFilterMode ? 'Filter groups below' : 'Show groups below'}
              </Button>
              <Link to={`/category/${detailCategory.id}`} onClick={closeDetail}>
                Open full category page
              </Link>
            </DetailActions>
          </DetailBody>
        )}
      </BaseModal>
    </>
  );
};

export default CategoriesList;
